import { Check } from "lucide-react";
import { useId } from "react";
import type { FC } from "react";

import { cn } from "@/lib/utils";

import { useWorkflows } from "../context/WorkflowsContext";
import type {
  WorkflowDef,
  WorkflowGroup as Group,
  WorkflowView
} from "../types";
import { WorkflowCard } from "./WorkflowCard";
import {
  RAIL_COLS,
  rowState,
  stepEdges,
  stepState,
  WorkflowStep
} from "./WorkflowStepper";

type Props = {
  group: Group;
  defs: WorkflowDef[];
  views: Map<string, WorkflowView>;
  /** The number of the group's first step, across the whole list. */
  start: number;
  first: boolean;
  last: boolean;
};

/**
 * One section of Case Automations as one container: its name and "2 of 3
 * Done" in a header row, then its workflow cards in a grid (one column when
 * the pane is narrow). The stepper's rail runs down the container's left edge
 * and on through its header row, so the groups read as one run of steps (see
 * `WorkflowStepper`). The container's surface is styled by density in
 * `workflow-cards.css`.
 */
export const WorkflowGroup: FC<Props> = ({
  group,
  defs,
  views,
  start,
  first,
  last
}) => {
  const w = useWorkflows();
  const titleId = useId();
  const required = defs.filter((d) => !d.optional);
  const done = required.filter(
    (d) => views.get(d.id)?.status === "done"
  ).length;
  return (
    <div
      role="group"
      aria-labelledby={titleId}
      className="workflow-group flex flex-col gap-(--wf-hgap) rounded-lg p-(--wf-gpad) ring-1 ring-line-2"
      data-testid={`workflow-group-${group.id}`}
      data-done={done === required.length ? "true" : undefined}
    >
      <div className={cn("workflow-group-head grid items-center", RAIL_COLS)}>
        <div className="relative self-stretch" aria-hidden>
          {first ? null : (
            <span className="absolute top-0 bottom-(--wf-hbridge) left-1/2 w-px -translate-x-1/2 bg-line-3" />
          )}
        </div>
        <div className="flex min-h-6 min-w-0 items-center gap-2">
          <h3
            id={titleId}
            className="min-w-0 truncate text-md font-medium text-fg"
          >
            {group.title}
          </h3>
          <span
            className="ml-auto flex shrink-0 items-center gap-1 text-sm font-medium text-fg-2 tabular-nums"
            data-testid={`workflow-group-count-${group.id}`}
          >
            {done === required.length ? (
              <Check className="size-3.5 text-success" aria-hidden />
            ) : null}
            {done} of {required.length} Done
          </span>
        </div>
      </div>
      <ol
        aria-label={`${group.title} Steps`}
        className={cn(
          "grid gap-y-2 @xl:grid-cols-[1rem_minmax(0,1fr)_minmax(0,1fr)] @xl:gap-x-3",
          RAIL_COLS
        )}
      >
        {defs.map((def, i) => {
          const v = views.get(def.id);
          if (!v) return null;
          const nextUp = !w.running && w.nextUp === def.id;
          const state = stepState(v, nextUp);
          const pair = i % 2 === 0 ? defs[i + 1] : undefined;
          const pairView = pair ? views.get(pair.id) : undefined;
          const wideState = pairView
            ? rowState([
                state,
                stepState(pairView, !w.running && w.nextUp === pair?.id)
              ])
            : undefined;
          return (
            <WorkflowStep
              key={def.id}
              index={i}
              number={start + i}
              def={def}
              view={v}
              state={state}
              wideState={wideState}
              edges={stepEdges(i, defs.length, first, last)}
            >
              <WorkflowCard def={def} view={v} nextUp={nextUp} />
            </WorkflowStep>
          );
        })}
      </ol>
    </div>
  );
};
